import { useEffect, useSyncExternalStore } from "react";
import { getFxRates, type FxRatesResponse } from "@/lib/api";

/**
 * hooks/useFxRates.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Semantic Intent Filters + Reference Currency Display V1.1, Parte F.
 *
 * Loads GET /marketplace/fx-rates ONCE for the whole app lifetime (a module-
 * level singleton fetch — every component calling useFxRates() shares the
 * SAME in-memory result reactively via useSyncExternalStore, the same
 * pattern usePreferences.ts uses for currency). Task 4's own requirement:
 * "cargar /marketplace/fx-rates una vez al iniciar la app o al montar
 * marketplace... sin refetch de experiencias" — changing the selected
 * currency later only re-renders price displays from these SAME cached
 * rates, never triggers a second call to this endpoint or to
 * /experiences/search.
 *
 * On failure (network error, non-2xx, etc.) `data` stays null and `error`
 * is true — callers (currencyConversion.ts's own convertPriceForDisplay)
 * already treat a missing/undefined rates map as "show the original price,
 * no conversion" — a failed fetch here never crashes a card, it just means
 * every price silently stays in its own original currency until rates
 * eventually load (or the next reload retries).
 */

interface FxRatesState {
  data: FxRatesResponse | null;
  isLoading: boolean;
  error: boolean;
}

let _state: FxRatesState = { data: null, isLoading: false, error: false };
let _fetchStarted = false;
const _listeners = new Set<() => void>();

function _emit(): void {
  for (const listener of _listeners) listener();
}

function _subscribe(listener: () => void): () => void {
  _listeners.add(listener);
  return () => { _listeners.delete(listener); };
}

function _getSnapshot(): FxRatesState {
  return _state;
}

function _ensureFetchStarted(): void {
  if (_fetchStarted) return;
  _fetchStarted = true;
  _state = { ..._state, isLoading: true };
  _emit();
  getFxRates()
    .then((data) => {
      _state = { data, isLoading: false, error: false };
      _emit();
    })
    .catch(() => {
      _state = { data: null, isLoading: false, error: true };
      _emit();
    });
}

/** Test-only seam — resets the module-level singleton so each test starts
 * from a cold, unfetched state. Never used by production code. */
export function _resetFxRatesStoreForTests(): void {
  _state = { data: null, isLoading: false, error: false };
  _fetchStarted = false;
}

export function useFxRates(): FxRatesState {
  const state = useSyncExternalStore(_subscribe, _getSnapshot, _getSnapshot);
  useEffect(() => { _ensureFetchStarted(); }, []);
  return state;
}
